<template>
  <div class="flex">
    <NImage
      v-if="src"
      v-bind="omit(attrs, 'style')"
      ref="imageRef"
      class="c-image"
      :fallback-src="effectiveFallbackSrc ?? undefined"
      :object-fit="objectFit"
      :src="src"
    />
    <img
      v-else-if="effectiveFallbackSrc"
      v-bind="omit(attrs, ['style', 'src'])"
      class="c-image"
      :src="effectiveFallbackSrc"
      :style="{ objectFit: objectFit }"
      @error="handleImageError"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useAttrs, onMounted, nextTick } from 'vue'
import { NImage } from 'naive-ui'
import { omit } from 'lodash-es'
import type { ComputedRef } from 'vue'

import { generateAIThumbnailUrl } from '@lib/util/image'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { getDefaultThumbnailUrl } from '@lib/utils/defaultThumbnail.util'
import type { FileContentTypeEnum } from '@openapi'

const emit = defineEmits<{ error: [] }>()
const attrs = useAttrs()
const imageRef = ref<InstanceType<typeof NImage> | null>(null)

// Get LaunchDarkly flags
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const props = withDefaults(
  defineProps<{
    src?: string | null
    fallbackSrc?: string | null
    aiImgDescription?: string
    objectFit?: 'cover' | 'contain' | 'scale-down'
    type?: 'default' | 'error'
    fileData?: { name?: string; content_type?: FileContentTypeEnum | null; content_extension?: string | null } | null
  }>(),
  {
    objectFit: 'cover',
    type: 'default',
    fileData: null,
  },
)

// Determine fallback source with AI generation if enabled
const effectiveFallbackSrc = computed(() => {
  // If fallbackSrc is provided, use it
  if (props.fallbackSrc) return props.fallbackSrc

  // If AI thumbnails are enabled and we have an image description, generate AI thumbnail URL
  if (launchDarkly.value.enable_ai_generated_thumbnails && props.aiImgDescription) {
    return generateAIThumbnailUrl(props.aiImgDescription)
  }

  if (props.fileData) {
    const defaultThumbnail = getDefaultThumbnailUrl(props.fileData as any)
    if (defaultThumbnail) return defaultThumbnail
  }

  // Otherwise return undefined
  return undefined
})

function handleImageError() {
  emit('error')

  if (effectiveFallbackSrc.value && imageRef.value) {
    nextTick(() => {
      const imgEl = imageRef.value?.$el?.querySelector('img')
      if (imgEl && imgEl.src !== effectiveFallbackSrc.value) {
        imgEl.src = effectiveFallbackSrc.value!
      }
    })
  }
}

// Ensure the AI thumbnail URL is properly set in Chrome
onMounted(() => {
  if (effectiveFallbackSrc.value && !props.src) {
    // Force Chrome to recognize the fallback source by ensuring DOM is updated
    nextTick(() => {
      if (imageRef.value && effectiveFallbackSrc.value) {
        const imgEl = imageRef.value.$el?.querySelector('img')
        if (imgEl && !imgEl.src) {
          imgEl.src = effectiveFallbackSrc.value
        }
      }
    })
  }

  // Add direct error listener
  if (props.src) {
    nextTick(() => {
      const imgEl = imageRef.value?.$el?.querySelector('img')
      if (imgEl) {
        imgEl.addEventListener('error', () => {
          handleImageError()
        })
      }
    })
  }
})
</script>
